import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { RETURNING, watchTunnel } from "../lib/tunnel-link";
import { useTaal } from "@/contexts/TaalContext";

/**
 * The gate in front of everything that needs somebody to be there.
 *
 * There is no third state. Either the app knows who is using it, from a token
 * or from being the only copy on somebody's own machine, or it sends them to
 * the screen where they can become somebody. What it may not do is render as
 * if a session exists: that is the shape of failure that hides itself, since
 * every page draws and every call behind it returns 401.
 *
 * Waiting is not the same as refused. While the answer is still coming this
 * shows nothing rather than a redirect, or a phone would bounce to the
 * sign-in screen for an instant on every single load.
 */
export default function RequireSession({ children }) {
  const { t } = useTaal();
  const { user, isLoading } = useAuth();
  const [tunnel, setTunnel] = useState({ where: "away" });
  useEffect(() => watchTunnel(setTunnel), []);

  // Opstart-fix, 12 oktober 2026 (besluit Frank): zoekt de telefoon zijn Mac,
  // dan staat dat er meteen, en niet een leeg scherm. Op de Mac zelf is er
  // geen weg terug en blijft dit, zoals altijd, een fractie van een seconde leeg.
  if (isLoading && tunnel.where === RETURNING) {
    return (
      <div className="min-h-screen flex items-center justify-center gap-2 px-4 text-sm text-muted-foreground"
           data-testid="mac-zoeken">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("Verbinding met je Mac zoeken…")}
      </div>
    );
  }
  if (isLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children ?? <Outlet />;
}
